import { useTranslation } from 'libs/i18n'
import { Badge } from 'shared/atoms'
import type { BadgeVariant } from 'shared/atoms/Badge/Badge'
import type { WithClassName } from 'shared/types/general.types'
import { cn } from 'utils/style'

export const BOOKING_STATUS = {
	RESERVED: 'RESERVED',
	CONFIRMED: 'CONFIRMED',
	ARRIVED: 'ARRIVED',
	NO_SHOW: 'NO_SHOW',
	OVERDUE: 'OVERDUE',
	COMPLETED: 'COMPLETED',
	CANCELLED: 'CANCELLED',
	ABANDONED: 'ABANDONED'
} as const

export type BookingStatusType = keyof typeof BOOKING_STATUS

type BookingStatusProps = WithClassName<{
	status: BookingStatusType
	prefix?: string
	uppercase?: boolean
	dot?: boolean
	dotSize?: 'sm' | 'md'
}>

export const BOOKING_STATUS_VARIANTS: Record<BookingStatusType, BadgeVariant> = {
	RESERVED: 'warning',
	CONFIRMED: 'primary',
	ARRIVED: 'success',
	NO_SHOW: 'warning',
	OVERDUE: 'danger',
	COMPLETED: 'muted',
	CANCELLED: 'danger',
	ABANDONED: 'muted'
} as const

export const BookingStatus = ({
	status,
	prefix,
	uppercase = true,
	dot,
	dotSize,
	className
}: BookingStatusProps) => {
	const { t } = useTranslation()
	const labels: Record<BookingStatusType, string> = {
		RESERVED: t('Reserved'),
		CONFIRMED: t('Confirmed'),
		ARRIVED: t('Arrived'),
		NO_SHOW: t('No show'),
		OVERDUE: t('Overdue'),
		COMPLETED: t('Completed'),
		CANCELLED: t('Cancelled'),
		ABANDONED: t('Abandoned')
	}

	return (
		<Badge
			uppercase={uppercase}
			prefix={prefix}
			dot={dot}
			dotSize={dotSize}
			variant={BOOKING_STATUS_VARIANTS[status] ?? 'muted'}
			className={cn(!uppercase && 'px-2.5 font-bold tracking-wide', className)}>
			{uppercase ? status.replace('_', ' ') : (labels[status] ?? status)}
		</Badge>
	)
}
